import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CreditCard, Package, Hotel, ShoppingBag, QrCode } from 'lucide-react';
import { TranslatedText } from './translated-text';
import { useAuth } from '../contexts/auth-context';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { MenuQrCode } from './menu-qr-code';

export function QuickAccessFloating() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  // Role-based visibility checks
  const canAccessPOS = () => {
    // Admin always has access
    if (user?.role === 'admin' || user?.role === 'super_admin') return true;
    // Waiter needs explicit access
    if (user?.role === 'waiter') {
      return (user as any).accessScope?.includes('POS');
    }
    // Others usually don't need POS shortcut unless specified
    return ['manager', 'cashier'].includes(user?.role || '');
  };

  const canAccessHotel = () => {
    // Admin always has access
    if (user?.role === 'admin' || user?.role === 'super_admin') return true;
    // Receptionist needs access
    if (user?.role === 'receptionist') return true;
    // Waiter needs explicit access
    if (user?.role === 'waiter') {
      return (user as any).accessScope?.includes('HOTEL');
    }
    return ['manager'].includes(user?.role || '');
  };

  // Only show Inventory to admin/manager
  const canAccessInventory = () => {
    return ['admin', 'super_admin', 'manager'].includes(user?.role || '');
  };

  // Only show Purchase Orders to admin/manager
  const canAccessPurchases = () => {
    return ['admin', 'super_admin', 'manager'].includes(user?.role || '');
  };

  // Waiters and Receptionists should NOT use floating buttons (per user request)
  if (user?.role === 'waiter' || user?.role === 'receptionist') {
    return null;
  }

  if (!user) return null;

  return (
    <div className="fixed right-2 bottom-52 z-[9990] flex flex-col gap-3 pointer-events-auto">
      <TooltipProvider>
        {/* Restaurant Menu QR Button */}
        <Tooltip>
          <TooltipTrigger asChild>
            <MenuQrCode 
              trigger={
                <Button
                  className={cn(
                    "rounded-full h-12 w-12 shadow-xl hover:shadow-2xl transition-all flex flex-col items-center justify-center gap-0 p-0 border-2",
                    location.pathname === '/restaurant-menu'
                      ? "bg-black hover:bg-gray-900 text-white border-black" 
                      : "bg-white hover:bg-gray-50 text-black border-gray-100 hover:border-gray-200"
                  )}
                >
                  <QrCode className="h-5 w-5" />
                  <span className="text-[9px] font-bold leading-none mt-0.5">Menu</span>
                </Button>
              }
            />
          </TooltipTrigger>
          <TooltipContent side="left">
            <p><TranslatedText text="Scan Restaurant Menu" /></p>
          </TooltipContent>
        </Tooltip>

        {/* Hotel Button */}
        {canAccessHotel() && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                onClick={() => navigate('/dashboard/hotel')}
                className={cn(
                  "rounded-full h-12 w-12 shadow-xl hover:shadow-2xl transition-all flex flex-col items-center justify-center gap-0 p-0 border-2",
                  location.pathname.includes('/dashboard/hotel') 
                    ? "bg-purple-600 hover:bg-purple-700 text-white border-purple-600" 
                    : "bg-white hover:bg-purple-50 text-purple-600 border-purple-100 hover:border-purple-200"
                )}
              >
                <Hotel className="h-5 w-5" />
                <span className="text-[9px] font-bold leading-none mt-0.5">Hotel</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left">
              <p><TranslatedText text="Hotel Management" /></p>
            </TooltipContent>
          </Tooltip>
        )}

        {/* Inventory Button */}
        {canAccessInventory() && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                onClick={() => navigate('/dashboard/inventory')}
                className={cn(
                  "rounded-full h-12 w-12 shadow-xl hover:shadow-2xl transition-all flex flex-col items-center justify-center gap-0 p-0 border-2",
                  location.pathname.includes('/dashboard/inventory') 
                    ? "bg-blue-600 hover:bg-blue-700 text-white border-blue-600" 
                    : "bg-white hover:bg-blue-50 text-blue-600 border-blue-100 hover:border-blue-200"
                )}
              >
                <Package className="h-5 w-5" />
                <span className="text-[9px] font-bold leading-none mt-0.5">Inv</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left">
              <p><TranslatedText text="Inventory" /></p>
            </TooltipContent>
          </Tooltip>
        )}

        {/* Purchase Orders Button */}
        {canAccessPurchases() && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                onClick={() => navigate('/dashboard/purchase-orders')}
                className={cn(
                  "rounded-full h-12 w-12 shadow-xl hover:shadow-2xl transition-all flex flex-col items-center justify-center gap-0 p-0 border-2",
                  location.pathname.includes('/dashboard/purchase-orders') 
                    ? "bg-orange-600 hover:bg-orange-700 text-white border-orange-600" 
                    : "bg-white hover:bg-orange-50 text-orange-600 border-orange-100 hover:border-orange-200"
                )}
              >
                <ShoppingBag className="h-5 w-5" />
                <span className="text-[9px] font-bold leading-none mt-0.5">P.Order</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left">
              <p><TranslatedText text="Purchase Orders" /></p>
            </TooltipContent>
          </Tooltip>
        )}

        {/* POS Button */}
        {canAccessPOS() && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                onClick={() => navigate('/dashboard/pos')}
                className={cn(
                  "rounded-full h-12 w-12 shadow-xl hover:shadow-2xl transition-all flex flex-col items-center justify-center gap-0 p-0 border-2",
                  location.pathname.includes('/dashboard/pos') 
                    ? "bg-green-600 hover:bg-green-700 text-white border-green-600" 
                    : "bg-white hover:bg-green-50 text-green-600 border-green-100 hover:border-green-200"
                )}
              >
                <CreditCard className="h-5 w-5" />
                <span className="text-[9px] font-bold leading-none mt-0.5">POS</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left">
              <p><TranslatedText text="Point of Sale" /></p>
            </TooltipContent>
          </Tooltip>
        )}
      </TooltipProvider>
    </div>
  );
}
